import './styles/main.css'
import './styles/driver-offers-runtime.css'
import './styles/entry-runtime.css'
import { store } from './lib/store'
import { init_theme_detection, toggle_theme, update_theme_toggle_button } from './lib/theme'
import { install_number_input_wheel_guard } from './lib/number-input-wheel-guard'

install_number_input_wheel_guard()
import { mount_navbar } from './components/navbar'
import { mount_error_bar } from './components/error-bar'
import { init_install_banner, sync_install_banner } from './components/install-banner'
import { router } from './router'
import { modal_alert, modal_confirm } from './components/modal'
import { init_push } from './lib/push'
import { fetch_config } from './lib/config'
import {
  activate_service_worker_update,
  hide_native_splash,
  prepare_development_browser_runtime,
  should_register_browser_service_worker,
} from './lib/runtime'
import { sync_rede_context } from './lib/rede-context'
import { escapeHtml } from './lib/utils'
import { mount_legal_footer } from './lib/legal'
import {
  CLIENT_ENTRY_PATH,
  is_client_entry_path,
  is_client_flow_path,
  parse_cnpj_from_path,
} from './lib/client-paths'
import { driver_path, is_driver_flow_path } from './lib/driver-paths'
import { is_ev_flow_path } from './lib/ev-paths'
import { resolve_entry_route } from './lib/entry-routing'
import {
  ensure_pwa_shortcut_manifest,
  init_pwa_shortcut,
  prepare_pwa_shortcut_path,
  restore_pwa_shortcut,
  schedule_pwa_shortcut_refresh,
} from './lib/pwa-shortcut'
import {
  clear_client_session,
  restore_client_session,
  restore_local_client_session,
  restore_local_driver_session,
  restore_driver_session,
  submit_client_session_navigation,
  submit_driver_session_navigation,
} from './lib/auth'
import { APP_BUILD_ID } from './lib/build'
import { PWA_BOOT_ERROR_CODE, render_runtime_error } from './components/runtime-error'
window.__toggle_theme = toggle_theme
window.__driver_scan_qr = null
window.__change_phone = async () => {
  const ok = await modal_confirm('Trocar telefone', 'Deseja trocar o número de telefone? Você será redirecionado para a tela inicial.', 'Sim, trocar', 'btn-warning')
  if (!ok) return
  try {
    await clear_client_session()
  } catch {
    modal_alert('Não foi possível desconectar', 'Verifique sua conexão e tente novamente.', 'warning')
    return
  }
  mount_navbar()
  update_theme_toggle_button()
  void router.navigate(CLIENT_ENTRY_PATH)
}

const CLIENT_CNPJ_SCOPED_KEYS = [
  'phone',
  'auth_token',
  'client_auth_token',
  'station_name',
  'station_location',
  'station_logo',
  'latitude',
  'longitude',
  'whatsapp_link',
  'voucher',
] as const

function clean_cnpj(value: unknown): string {
  const digits = (typeof value === 'string' ? value : typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint' ? String(value) : '').replace(/\D/g, '')
  return digits.length === 14 ? digits : ''
}

function reset_client_session_for_cnpj_switch(next_cnpj: string): void {
  const current_cnpj = clean_cnpj(store.get('cnpj'))
  if (!current_cnpj || current_cnpj === next_cnpj) return

  for (const key of CLIENT_CNPJ_SCOPED_KEYS) {
    store.set(key, null)
  }
}

function set_client_cnpj_from_route(cnpj: string): void {
  reset_client_session_for_cnpj_switch(cnpj)
  store.set('cnpj', cnpj)
}

function shell_meta_content(name: string): string {
  return String(document.querySelector(`meta[name="${name}"]`)?.getAttribute('content') || '').trim()
}

function hydrate_station_shell_brand(path: string): void {
  const cnpj = parse_cnpj_from_path(path)
  if (!cnpj || shell_meta_content('app-brand-context') !== 'station') return

  set_client_cnpj_from_route(cnpj)
  const station_name = shell_meta_content('app-brand-name')
  const station_logo = shell_meta_content('app-brand-logo')
  if (station_name) store.set('station_name', station_name)
  if (station_logo) store.set('station_logo', station_logo)
}

function current_location_path(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`
}

function clean_path(path: string): string {
  const withoutQuery = String(path || '').split('?')[0] ?? ''
  return (withoutQuery.split('#')[0] ?? '') || '/'
}

function is_ai_preview(path: string): boolean {
  return clean_path(path) === '/ai'
}

function sync_guard_redirect_history(next_path: string, push: boolean): void {
  if (push || current_location_path() === next_path) return
  window.history.replaceState({}, '', next_path)
}

function should_prepare_pwa_navigation(path: string, push: boolean): boolean {
  if (is_ai_preview(path)) return false
  if (!push) return false
  if (is_driver_flow_path(path)) return !!store.get('driver_phone')
  return !!store.get('phone') && !!store.get('auth_token') && !!store.get('cnpj')
}

function sync_client_cnpj(path: string): void {
  const cnpj = parse_cnpj_from_path(path)
  if (!cnpj) return
  set_client_cnpj_from_route(cnpj)
}

function show_content(): void {
  const loading = document.getElementById('loading-section')
  const container = document.getElementById('content-container')
  if (loading) loading.classList.add('d-none')
  if (container) {
    container.classList.remove('app-hidden')
    container.classList.remove('d-none')
  }
}

function render_boot_failure(err: unknown): void {
  console.error(`[${PWA_BOOT_ERROR_CODE}] Init failed:`, err)
  const loading = document.getElementById('loading-section')
  const container = document.getElementById('content-container')
  const target = container || loading
  if (!target) return
  if (loading && loading !== target) loading.classList.add('d-none')
  target.classList.remove('app-hidden')
  target.classList.remove('d-none')
  render_runtime_error(
    target,
    PWA_BOOT_ERROR_CODE,
    'Não foi possível iniciar o aplicativo',
    'Tente novamente. Se continuar, envie este código ao suporte.',
    {
      phase: 'bootstrap',
      message: err instanceof Error ? err.message : err,
      stack: err instanceof Error ? err.stack : undefined,
    },
  )
}

function render_unconfigured_portal(host: string): void {
  mount_error_bar()
  const navbar = document.getElementById('navbar')
  if (navbar) navbar.innerHTML = ''
  const container = document.getElementById('content-container')
  if (container) {
    container.innerHTML = `
      <div class="container py-5">
        <section class="card border-0 shadow-sm mx-auto" style="max-width: 34rem">
          <div class="card-body p-4 text-center">
            <div class="rounded-circle bg-warning bg-opacity-10 text-warning d-inline-flex align-items-center justify-content-center mb-3" style="width:56px;height:56px">
              <i class="fas fa-triangle-exclamation fa-lg"></i>
            </div>
            <h1 class="h5 fw-bold mb-2">Portal não configurado</h1>
            <p class="text-muted mb-0">O domínio ${escapeHtml(host || 'informado')} ainda não está vinculado a uma rede.</p>
          </div>
        </section>
      </div>`
  }
  show_content()
}

const original_navigate = router.navigate.bind(router)
router.navigate = async (path: string, push = true): Promise<void> => {
  // O CNPJ da URL define o escopo antes da decisão de rota. Se ele mudou,
  // set_client_cnpj_from_route limpa a sessão anterior e impede que uma
  // autenticação de outro posto atravesse direto para /combustiveis.
  sync_client_cnpj(path)
  if (is_driver_flow_path(path) || is_ev_flow_path(path)) {
    store.set('auth_token', store.get('driver_auth_token'))
  }
  let next_path = resolve_entry_route(path, {
    client_phone: !!store.get('phone'),
    client_auth: !!store.get('client_auth_token')
      && store.get('auth_token') === store.get('client_auth_token')
      // O formulário processa o telefone explícito antes de reutilizar a sessão.
      && !new URL(path, window.location.href).searchParams.has('tel'),
    client_cnpj: clean_cnpj(store.get('cnpj')) || null,
    driver_phone: !!store.get('driver_phone'),
    driver_auth: !!store.get('driver_auth_token'),
  })

  // Fluxo motorista: navigation guard separado
  if (is_driver_flow_path(next_path)) {
    if (should_prepare_pwa_navigation(next_path, push)) {
      try {
        next_path = await prepare_pwa_shortcut_path(next_path)
      } catch (err) {
        console.warn('[pwa] falha ao preparar navegacao do fluxo motorista:', err)
      }
    }
    sync_guard_redirect_history(next_path, push)
    await original_navigate(next_path, push)
    mount_navbar()
    mount_legal_footer()
    sync_install_banner()
    update_theme_toggle_button()
    schedule_pwa_shortcut_refresh()
    return
  }

  // Fluxo cliente normal
  if (is_client_flow_path(next_path)) sync_client_cnpj(next_path)
  if (should_prepare_pwa_navigation(next_path, push)) {
    try {
      next_path = await prepare_pwa_shortcut_path(next_path)
    } catch (err) {
      console.warn('[pwa] falha ao preparar navegacao do fluxo cliente:', err)
    }
  }
  sync_guard_redirect_history(next_path, push)
  await original_navigate(next_path, push)
  mount_navbar()
  mount_legal_footer()
  sync_install_banner()
  update_theme_toggle_button()
  if (!is_ai_preview(next_path)) schedule_pwa_shortcut_refresh()
}

let client_bfcache_resume: Promise<void> | null = null

async function resume_client_entry_from_bfcache(): Promise<void> {
  const path = current_location_path()
  if (!is_client_entry_path(path)) return

  const expected_cnpj = parse_cnpj_from_path(path) || undefined
  const cookie_restored = await restore_client_session(expected_cnpj)
  if (!cookie_restored) await restore_local_client_session(expected_cnpj)
  await router.navigate(path, false)
  void ensure_pwa_shortcut_manifest()
}

window.addEventListener('pageshow', event => {
  if (!event.persisted || client_bfcache_resume) return
  client_bfcache_resume = resume_client_entry_from_bfcache()
    .catch(err => console.warn('[auth] falha ao revalidar sessão após voltar:', err))
    .finally(() => { client_bfcache_resume = null })
})

// O shell arma um watchdog de bootstrap antes deste bundle existir. Marcar o
// runtime como pronto tem de desarmá-lo no mesmo passo, senão ele continua vivo e
// pode sobrescrever a tela já montada.
// A primeira tela montada libera a troca do service worker (PWA.md).
let resolve_runtime_ready: () => void = () => {}
const runtime_ready = new Promise<void>(resolve => { resolve_runtime_ready = resolve })

function mark_runtime_ready(): void {
  window.__app_runtime_ready = true
  window.__app_boot_watchdog_stop?.()
  resolve_runtime_ready()
}

async function init() {
  // O primeiro acesso do ai-dev abre a prévia pedida. Deep links existentes
  // com CNPJ/PIN continuam no fluxo original; produção não inclui esta entrada.
  if (import.meta.env.DEV && window.location.hostname === 'ai-dev.nabomba.com'
    && window.location.pathname === '/' && !window.location.search) {
    window.history.replaceState({}, '', '/ai')
  }
  const preview = is_ai_preview(current_location_path())
  init_theme_detection()
  hydrate_station_shell_brand(current_location_path())
  const rede_context = await sync_rede_context()
  if (rede_context.mode === 'unconfigured') {
    render_unconfigured_portal(rede_context.host)
    mark_runtime_ready()
    await hide_native_splash()
    return
  }
  mount_error_bar()
  mount_navbar()
  if (!preview) init_install_banner()
  sync_install_banner()
  update_theme_toggle_button()
  const pinned_path = preview ? null : await restore_pwa_shortcut()

  const initial_path = pinned_path || current_location_path()
  const initial_cnpj = is_client_flow_path(initial_path) || (clean_path(initial_path) === '/' && !!parse_cnpj_from_path(initial_path))
    ? parse_cnpj_from_path(initial_path)
    : null
  if (initial_cnpj) set_client_cnpj_from_route(initial_cnpj)
  if (is_client_flow_path(initial_path)) {
    const restored = await restore_client_session(initial_cnpj || undefined)
    if (!restored && await restore_local_client_session(initial_cnpj || undefined)) {
      const cnpj = clean_cnpj(store.get('cnpj'))
      // A confirmação do cookie é best-effort. Alguns navegadores bloqueiam a
      // navegação programática; ela nunca pode impedir a montagem da interface.
      if (cnpj) submit_client_session_navigation(`/combustiveis?cnpj=${encodeURIComponent(cnpj)}`)
    }
  }
  const initial_is_driver_flow = is_driver_flow_path(initial_path)
    || is_ev_flow_path(initial_path)
    || (clean_path(initial_path) === '/' && !initial_cnpj)
  if (initial_is_driver_flow) {
    const restored = await restore_driver_session()
    if (!restored && await restore_local_driver_session()) {
      const context_path = clean_path(initial_path) === '/' ? '/ofertas' : initial_path
      const return_path = is_ev_flow_path(context_path) ? context_path : driver_path('offers', context_path)
      // Se o POST principal for bloqueado ou throttled pelo navegador, a sessão
      // local continua sujeita à validação das APIs e a tela ainda precisa abrir.
      submit_driver_session_navigation(return_path)
    }
  }

  const container = document.getElementById('content-container')
  if (!container) throw new Error('content-container ausente no DOM')
  router.setup(container)
  if (!preview) init_pwa_shortcut()
  show_content()
  await router.navigate(initial_path, false)
  mark_runtime_ready()
  await hide_native_splash()
  if (!preview) void ensure_pwa_shortcut_manifest()
}

void (async () => {
  if (!await prepare_development_browser_runtime()) return
  await init()
})().catch(err => {
  render_boot_failure(err)
  void hide_native_splash()
})

if (should_register_browser_service_worker()) {
  navigator.serviceWorker.register(`/sw.js?v=${encodeURIComponent(APP_BUILD_ID)}`, {
    updateViaCache: 'none',
  }).then(registration => {
    // Força somente a verificação do script. O worker novo espera e só assume
    // depois que a primeira tela montou, sem interromper a abertura atual.
    void registration.update().catch(() => {})
    activate_service_worker_update(registration, runtime_ready)
    // Listen for push messages from service worker
    navigator.serviceWorker.addEventListener('message', (event) => {
      const data = event.data as { type?: string; title?: string; body?: string }
      console.log('[app] SW message recebido:', data)
      if (data.type === 'push-notification') {
        console.log('[app] mostrando modal push:', data.title, data.body)
        modal_alert(data.title || 'Notificacao', data.body || '', 'info')
      }
    })

    // Check for pending notification from Cache API
    void (async () => {
      const cache = await caches.open('notification-pending')
      const resp = await cache.match('/_pending_notification')
      if (!resp) return
      await cache.delete('/_pending_notification')
      const data = await resp.json() as { title?: string; body?: string }
      modal_alert(data.title || 'Notificacao', data.body || '', 'info')
    })()

    // Init push when authenticated (now or later)
    const try_push = () => {
      fetch_config().then(cfg => {
        if (cfg.vapid_public_key) void init_push(cfg.vapid_public_key)
      }).catch(() => {})
    }
    if (store.get('auth_token')) try_push()
    store.on('auth_token', (val) => { if (val) try_push() })
  }).catch(() => {})
}
